/**
 * WikiLinkAutocomplete — Autocomplete dropdown triggered by `[[` in the editor.
 *
 * Queries BFF searchPagesByTitle with the typed text and displays matching pages.
 * On selection, inserts a WikiLinkNode inline content into the editor.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import * as pagesApi from '../../api/pages-api';

interface WikiLinkAutocompleteProps {
  readonly editor: BlockNoteEditor<any, any, any>;
  readonly libraryId: string;
  readonly position: { top: number; left: number } | null;
  readonly searchText: string;
  readonly onSelect: (pageId: string, title: string) => void;
  readonly onClose: () => void;
}

interface SearchResult {
  readonly pageId: string;
  readonly title: string;
}

export function WikiLinkAutocomplete({
  editor: _editor,
  libraryId,
  position,
  searchText,
  onSelect,
  onClose,
}: WikiLinkAutocompleteProps): React.ReactElement | null {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  useEffect(() => {
    if (!searchText || !libraryId) {
      setResults([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await pagesApi.searchPagesByTitle(libraryId, searchText);
        setResults(response.data ?? []);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchText, libraryId]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!position) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex].pageId, results[selectedIndex].title);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [position, results, selectedIndex, onSelect, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (!position) return null;

  return (
    <div
      ref={containerRef}
      className="absolute z-50 w-64 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg"
      style={{ top: position.top, left: position.left }}
      role="listbox"
      aria-label="Wiki link suggestions"
    >
      {loading && (
        <div className="px-3 py-2 text-sm text-muted-foreground">Searching...</div>
      )}

      {!loading && results.length === 0 && searchText && (
        <div className="px-3 py-2 text-sm text-muted-foreground">No pages found</div>
      )}

      {!loading &&
        results.map((result, index) => (
          <button
            key={result.pageId}
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors
              ${index === selectedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
            `}
            onClick={() => onSelect(result.pageId, result.title)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="truncate block">{result.title}</span>
          </button>
        ))}
    </div>
  );
}

/**
 * Hook to detect `[[` trigger in the editor and manage autocomplete state.
 */
export function useWikiLinkTrigger(editor: BlockNoteEditor<any, any, any> | null) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [triggerBlock, setTriggerBlock] = useState<string | null>(null);

  useEffect(() => {
    if (!editor) return;

    // Monitor text changes for [[ trigger
    const handleChange = () => {
      try {
        const cursor = editor.getTextCursorPosition();
        if (!cursor || !cursor.block) return;

        const block = cursor.block;
        // Get text content from block's inline content
        const textContent = extractTextFromBlock(block);

        const triggerIndex = textContent.lastIndexOf('[[');
        if (triggerIndex === -1) {
          if (isOpen) {
            setIsOpen(false);
            setSearchText('');
            setPosition(null);
            setTriggerBlock(null);
          }
          return;
        }

        // Check if trigger is closed (has matching ]])
        const afterTrigger = textContent.slice(triggerIndex + 2);
        if (afterTrigger.includes(']]')) {
          if (isOpen) {
            setIsOpen(false);
            setSearchText('');
            setPosition(null);
            setTriggerBlock(null);
          }
          return;
        }

        // Extract search text after [[
        const query = afterTrigger;
        setSearchText(query);
        setTriggerBlock(block.id);

        if (!isOpen) {
          // Position the dropdown near the cursor
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            setPosition({
              top: rect.bottom + 4,
              left: rect.left,
            });
          }
          setIsOpen(true);
        }
      } catch {
        // Ignore errors in text extraction
      }
    };

    editor.onChange(handleChange);

    return () => {
      // BlockNote doesn't expose an "off" for onChange in this version,
      // but the component will unmount and re-create as needed.
    };
  }, [editor, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchText('');
    setPosition(null);
    setTriggerBlock(null);
  }, []);

  return { isOpen, searchText, position, triggerBlock, close };
}

/**
 * Extract plain text from a BlockNote block's content array.
 */
function extractTextFromBlock(block: any): string {
  if (!block.content || !Array.isArray(block.content)) return '';
  return block.content
    .map((item: any) => {
      if (item.type === 'text') return item.text || '';
      if (item.type === 'link') {
        return (item.content || []).map((c: any) => c.text || '').join('');
      }
      return '';
    })
    .join('');
}
